import { useNavigate } from "react-router-dom";
import { Heart, BedDouble, Bath, Car, Maximize, MapPin, BadgeCheck } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatINR, imgUrl } from "@/lib/format";
import { toast } from "sonner";

export default function PropertyCard({ property, index = 0 }) {
  const navigate = useNavigate();
  const { user, favorites, toggleFavorite, login } = useAuth();
  const isFav = favorites.includes(property.id);

  const handleFav = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error("Please sign in to save properties");
      login();
      return;
    }
    const favorited = await toggleFavorite(property.id);
    toast.success(favorited ? "Added to shortlist" : "Removed from shortlist");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      onClick={() => navigate(`/properties/${property.id}`)}
      data-testid={`property-card-${property.id}`}
      className="group cursor-pointer bg-white border border-slate-200 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:border-blue-200 transition-all duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={imgUrl(property.images?.[0])}
          alt={property.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {!property.sold && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wide bg-navy text-white">
            {property.listing_type === "rent" ? "For Rent" : "For Sale"}
          </span>
        )}
        {property.sold && (
          <div className="absolute inset-0 bg-slate-900/45 flex items-center justify-center">
            <span data-testid={`sold-badge-${property.id}`} className="px-4 py-1.5 rounded-md text-sm font-bold uppercase tracking-widest bg-gold text-navy -rotate-6 shadow-lg">
              Sold
            </span>
          </div>
        )}
        <button
          data-testid={`fav-btn-${property.id}`}
          onClick={handleFav}
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:scale-110 transition-transform"
        >
          <Heart className={`h-5 w-5 ${isFav ? "fill-gold text-gold-dark" : "text-slate-600"}`} />
        </button>
      </div>
      <div className="p-5">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-display text-2xl font-bold text-slate-900">
            {formatINR(property.price)}
            {property.listing_type === "rent" && <span className="text-sm font-medium text-slate-500">/mo</span>}
          </p>
        </div>
        <h3 className="mt-1.5 font-medium text-slate-900 line-clamp-1">{property.title}</h3>
        <div className="mt-1 flex items-center justify-between">
          <p className="flex items-center text-sm text-slate-500">
            <MapPin className="h-3.5 w-3.5 mr-1" /> {property.locality}, {property.city}
          </p>
          {property.owner_verified && (
            <span data-testid={`verified-badge-${property.id}`} className="flex items-center gap-1 text-xs font-semibold text-navy bg-navy/10 px-2 py-0.5 rounded-full shrink-0">
              <BadgeCheck className="h-3.5 w-3.5" /> Verified
            </span>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-5 text-sm text-slate-600">
          <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-navy" /> {property.bhk}</span>
          <span className="flex items-center gap-1.5"><Bath className="h-4 w-4 text-navy" /> {property.bathrooms}</span>
          <span className="flex items-center gap-1.5"><Car className="h-4 w-4 text-navy" /> {property.car_spaces ?? 1}</span>
          <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4 text-navy" /> {property.area_sqft} sqft</span>
        </div>
        <p className="mt-2 text-xs uppercase tracking-wide text-slate-400">{property.property_type}</p>
      </div>
    </motion.div>
  );
}
